import { useEffect, useState } from "react";

import Util from "@lululemon/ecom-common/src/lib/util";
import * as atgApi from "@lululemon/ecom-common/src/services/atg";
import { getUsrLocaleStandard } from "@lululemon/ecom-common/src/util/locale-utils";

import {
  getEcId,
  getMCId,
} from "@lululemon/ecom-common/src/lib/signal-creator";
import Cookies from "js-cookie";
import {
  fetchGraphQLRecommenders,
  setUserToItemsQueryParams,
  setItemToItemsQueryParams,
} from "@lululemon/ecom-common/src/graphql/fetchRecommenders";

export const util = new Util("util/homepage-recs");

export const getSanitizedEcid = () => {
  const ecid = getEcId() || "";
  const mcid = getMCId(window?.location?.search);
  const sanitizedEcid = ecid.startsWith("MCMID") ? ecid.split("|")[1] : ecid;
  return mcid || sanitizedEcid;
};

export const getRecsURL = ({ itemCount = 8, userId }) => {
  // this is a temporary fix to parameterize the API call.
  // Right now the API only honours locales en_US and en_CA
  // and will fallback to en_US if the locale is not supported.
  // We want to patch this behaviour by instead falling back to en_CA if the locale === fr_CA.
  // This way, we can at least get the CAN currency code from this EP.
  const locale =
    Cookies.get("UsrLocale") === "fr_CA" ? "en_CA" : Cookies.get("UsrLocale");

  const hostName = atgApi.getHostName() || "shop.lululemon.com";
  const modelId = "UserItemModelV1";
  return `https://${hostName}/api/r/products?type=userToItems&userId=${userId}&item-count=${itemCount}&fallback=true&modelId=${modelId}&dataSource=datascience&locale=${locale}`;
};

const useRecommenders = ({
  itemCount = 8,
  isHomeRecsEnabled = false,
  recsOverrideUrl = "",
  shouldUseGraphQL,
  graphqlClient,
  aiCampaignConfig,
}) => {
  const [recs, setRecs] = useState(false);

  useEffect(() => {
    // if recs are already set, don't fetch again
    // if recs are disabled, don't fetch
    // undefined check because this will fire before the flag is set
    if (!isHomeRecsEnabled || recs || typeof shouldUseGraphQL === "undefined")
      return;
    const sanitizedEcid = getSanitizedEcid();
    const url =
      recsOverrideUrl || getRecsURL({ itemCount, userId: sanitizedEcid });
    if (shouldUseGraphQL) {
      let locale = getUsrLocaleStandard().replace("-", "_");
      if (!aiCampaignConfig) {
        locale = locale === "fr_CA" ? "en_CA" : locale;
      }
      const queryParams = (
        aiCampaignConfig ? setItemToItemsQueryParams : setUserToItemsQueryParams
      )({
        userId: sanitizedEcid,
        itemCount: 8,
        fallback: true,
        modelId: aiCampaignConfig
          ? aiCampaignConfig?.modelId || "CatTrendingGiftingV1"
          : "UserItemModelV1",
        ...(aiCampaignConfig
          ? { productId: aiCampaignConfig?.itemId || "gifting" }
          : {}),
        locale,
      });
      fetchGraphQLRecommenders(
        aiCampaignConfig ? "itemToItems" : "userToItems",
        graphqlClient,
        queryParams,
        {
          message: "Error fetching userToItems Recommendations",
          tags: {
            componentName: "useRecommendersHook",
            functionName: "fetchGraphQLRecommenders",
          },
          onError: (err) => {
            setRecs({
              hasError: true,
              error: err,
            });
          },
        }
      ).then((data) => {
        if (data) {
          setRecs({
            recData: data,
            products: data.included?.[0].items || [],
            ecid: sanitizedEcid,
          });
        }
      });
    } else {
      util
        .api(
          {
            url,
            redirect: true,
          },
          "warning",
          "Recommenders-API"
        )
        .then(({ data }) => {
          setRecs({
            recData: data,
            products: data.included?.[0].items || [],
            ecid: sanitizedEcid,
          });
        })
        .catch((err) => {
          setRecs({
            hasError: true,
            error: err,
          });
          console.error("Error fetching userToItems Recommendations", err);
        });
    }
  }, [isHomeRecsEnabled, itemCount, recs, shouldUseGraphQL]);

  return recs;
};

export default useRecommenders;
